// src/pages/LiveConversations.jsx
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, getTokens } from '../lib/api.js';
import { priorityConfig } from '../components/PriorityBadge.jsx';

function timeAgo(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

const STATUS_LABEL = { assigned: 'Assigned', active: 'Active', escalated: 'Escalated' };

export default function LiveConversations() {
  const [cases, setCases] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const body = await api('/cases?live=true');
      setCases(body.cases);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live nudge — reuses the same 'queue:global' channel a supervisor
  // wallboard would watch (Module 5). Requires reports.view; if the
  // logged-in role doesn't have it, this simply won't connect and the
  // page still works via the initial load + manual refresh.
  //
  // Reconnects every 10 minutes with a freshly-fetched token — access
  // tokens expire at 15 minutes, and a connection opened once with a
  // token baked into the URL goes silently dead past that point with no
  // error surfaced anywhere. (This is the same bug pattern already found
  // and fixed in Dashboard.jsx; it just hadn't been carried over here.)
  useEffect(() => {
    let es;
    let renewTimer;

    function connect() {
      const { accessToken } = getTokens();
      if (!accessToken) return;
      es = new EventSource(`/api/realtime/queue-stream?access_token=${encodeURIComponent(accessToken)}`);
      es.onmessage = () => load();
      es.onerror = () => es.close();
    }

    connect();
    renewTimer = setInterval(() => { es?.close(); connect(); }, 10 * 60 * 1000);
    return () => { clearInterval(renewTimer); es?.close(); };
  }, [load]);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-charcoal">Live Conversations</h1>
        <span className="flex items-center gap-1.5 text-xs text-ink/50">
          <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
          Updates live
        </span>
      </div>
      <p className="text-xs text-ink/50 mt-1">Every case currently assigned, active, or escalated — sorted by most recent activity. Open any conversation to observe or take over.</p>

      {error && <p className="mt-3 text-sm text-p1">{error}</p>}

      <div className="mt-5 space-y-2">
        {cases.length === 0 && (
          <div className="rounded-lg border border-dashed border-ink/20 p-8 text-center text-sm text-ink/50">
            No live conversations right now.
          </div>
        )}
        {cases.map((c) => {
          const edge = priorityConfig(c.priority).edge;
          return (
            <Link
              key={c.id}
              to={`/cases/${c.id}`}
              className="flex items-stretch gap-4 rounded-lg border border-ink/10 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              <div className={`w-1.5 shrink-0 ${edge} ${c.priority === 1 ? 'animate-p1-pulse' : ''}`} />
              <div className="flex-1 py-3 pr-4 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm text-ink/70">{c.case_ref}</span>
                  <span className="text-xs text-ink/50">{timeAgo(c.updated_at)}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm font-medium">{STATUS_LABEL[c.status] ?? c.status}</span>
                  {c.category && <span className="text-xs text-ink/50">· {c.category.replace('_', ' ')}</span>}
                </div>
                <div className="mt-1 text-xs text-ink/60">
                  {c.assigned_staff_name ? `With ${c.assigned_staff_name}` : 'Unassigned'}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
